import { ArgumentError } from '../utils/errors';
import { BaseAPIModel, fetcher } from '@weezevent/weezjs-core';
import { capitalizeFirstLetter } from '../pages/attendee/detail/components/utils';
import moment from 'moment-timezone';

export default class SessionModel extends BaseAPIModel {
    fields() {
        return {
            uuid: { primaryKey: true, readOnly: true }
        };
    }

    get quota_total() {
        if (this.object.quota_total) {
            return this.object.quota_total;
        }
        if (!this.object.quota) {
            return null;
        }

        return this.object.quota.total;
    }

    get quota_taken() {
        if (this.object.quota_taken) {
            return this.object.quota_taken;
        }
        if (!this.object.quota) {
            return null;
        }

        return this.object.quota.taken;
    }

    get quota_booked() {
        if (!this.object.quota) {
            return null;
        }

        return this.object.quota.booked;
    }

    get quota_booking_in_progress() {
        if (!this.object.quota) {
            return null;
        }

        return this.object.quota.booking_in_progress;
    }

    countSessionsByDays(params) {
        if (!params.organizationId || !params.eventId) {
            throw new ArgumentError(`[Model][SessionModel][countSessionsByDays] organizationId and eventId are mandatory`);
        }
        const url = `/ticket/organizations/${params.organizationId}/events/${params.eventId}/sessions/count_sessions_by_days`;
        const queryParams = params?.query
            ? Object.entries(params.query)
                  ?.map(([k, v]) => `${k}=${v}`)
                  .join('&')
            : null;
        return fetcher.get(url + (queryParams ? `?${queryParams}` : ''));
    }

    static listUrl({ organizationId, eventId }) {
        if (!organizationId || !eventId) {
            throw new ArgumentError(`[Model][AttendeeModel][getAll] organizationId and eventId are mandatory`);
        }

        return `/ticket/organizations/${organizationId}/events/${eventId}/sessions`;
    }

    static perform_action(action, objects, params) {
        if (!this[`perform_action_${action}`]) {
            throw new Error(`No action named ${action}`);
        }
        return this[`perform_action_${action}`](objects, params);
    }

    static perform_action_enable(objects, { organization, event }) {
        const data = { session_ids: objects.map(o => o.uuid) };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/enable`, { data: data });
    }

    static perform_action_disable(objects, { organization, event }) {
        const data = { session_ids: objects.map(o => o.uuid) };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/disable`, { data: data });
    }

    static perform_action_tax(objects, { organization, event, tax_id }) {
        const data = { session_ids: objects.map(o => o.uuid), tax_id: tax_id };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/tax`, { data: data });
    }

    static perform_action_link_rates(objects, { organization, event, rate_ids }) {
        const data = { session_ids: objects.map(o => o.uuid), rate_ids };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/rates`, { data: data });
    }

    static formatRateQuotas(quotas) {
        let _quotas = [];
        Object.entries(quotas).forEach(([key, value]) => {
            _quotas.push({ rate_id: key, quota: value });
        });

        return _quotas;
    }

    static formatRateGroupQuotas(quotas) {
        let _quotas = [];
        Object.entries(quotas).forEach(([key, value]) => {
            _quotas.push({ rate_group_id: key, quota: value });
        });

        return _quotas;
    }

    static perform_action_quotas(objects, { organization, event, quota, rate_quotas, group_quotas }) {
        const data = {
            session_ids: objects.map(o => o.uuid),
            quota,
            rate_quotas: this.formatRateQuotas(rate_quotas),
            group_quotas: this.formatRateGroupQuotas(group_quotas)
        };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/quotas`, { data: data });
    }

    static perform_action_delete(objects, { organization, event }) {
        const data = { session_ids: objects.map(o => o.uuid) };
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/delete`, { data: data });
    }

    static perform_action_generate_sessions(data, { organization, event }) {
        if (!Object.keys(data.rates).length) {
            data.rates = null;
        }

        if (!Object.keys(data.rate_groups).length) {
            data.rate_groups = null;
        }
        return fetcher.post(`/ticket/organizations/${organization.id}/events/${event.id}/sessions/generate`, { data: data });
    }

    static sessionRemainingQuota = ({ session, rate = null }) => {
        let quotasArray = [];

        //Check rate quota
        if (session.rates && rate) {
            let rateQuota = session.rates.find(r => r.rate_id === rate.id);
            if (rateQuota?.quota) {
                quotasArray.push(rateQuota.quota.total - rateQuota.quota.taken);
            }
        }

        //Check group quota
        if (session.rate_groups && rate?.group) {
            let groupQuota = session.rate_groups.find(g => g.id === rate.group.id);
            if (groupQuota?.quota && groupQuota.quota.total) {
                quotasArray.push(groupQuota.quota.total - groupQuota.quota.taken);
            }
            //Check parent group quota
            if (rate.group.parent) {
                let parentGroupQuota = session.rate_groups.find(g => g.id === rate.group.parent);
                if (parentGroupQuota?.quota && parentGroupQuota.quota.total) {
                    quotasArray.push(parentGroupQuota.quota.total - parentGroupQuota.quota.taken);
                }
            }
        }

        //Check global quota
        if (session.quota && session.quota.total) {
            quotasArray.push(session.quota.total - session.quota.taken);
        }

        return quotasArray.length ? Math.min(...quotasArray) : null;
    };

    // Get the select options object for this tree with groups
    static toOptions({ sessions = null, timezone = 'Europe/Paris', locale = 'en-GB', onlyFuture = false, extraSession = null, remainingQuota = null, className = null }) {
        if (!sessions) {
            return [];
        }

        let sessionsSorted = sessions.sort((s1, s2) => new Date(s1.start_date).getTime() - new Date(s2.start_date).getTime());

        let nowTimestamp = new Date().getTime();
        //Group by YYYY-MM
        let tmp = sessionsSorted.reduce((acc, obj) => {
            let _obj = { ...obj.object };

            //Filters only future sessions
            if (onlyFuture && new Date(_obj.start_date).getTime() <= nowTimestamp) {
                return acc;
            }

            //Localize start_date
            let dateLocalizedStart = moment.utc(_obj.start_date).tz(timezone);

            _obj.start_date_localized = dateLocalizedStart.format('YYYY-MM-DD HH:mm');
            _obj.text = Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(dateLocalizedStart));
            _obj.value = _obj.uuid;

            //Set remainingQuota by custom function
            _obj.remaining_quota = remainingQuota ? remainingQuota(_obj) : null;
            _obj.disabled = Boolean(_obj.remaining_quota === 0);

            if (extraSession) {
                _obj.component = extraSession(_obj);
            }

            if (className) {
                _obj.classname = className(_obj);
            }

            let keyYearMonth = dateLocalizedStart.format('YYYY-MM');
            _obj.parent = keyYearMonth;

            acc[keyYearMonth] = acc[keyYearMonth] || [];
            acc[keyYearMonth].push(_obj);

            return acc;
        }, {});

        //Construct tree sessions
        return Object.entries(tmp).reduce((accu, [yearMonth, sessions]) => {
            let label = capitalizeFirstLetter(new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(sessions[0].start_date_localized)));

            accu.push({
                text: label,
                value: yearMonth,
                children: sessions
            });

            return accu;
        }, []);
    }

    // Get the select options object for this rate
    toOption(custom) {
        return {
            text: this.name,
            value: this.pk,
            ...custom?.(this)
        };
    }

    get text() {
        return this.name;
    }
    get date() {
        return this.start_date;
    }
    get time() {
        return this.start_time ?? 0;
    }
    get title() {
        // Séance du {this.date} à {this.time}
        return this.name;
    }

    get ratesIdsIndexes() {
        return Object.fromEntries((this.ratesIds ?? []).map(id => [id, id]));
    }

    get ratesIds() {
        return this.rates.map(rate => rate.id);
    }

    get selectedRates() {
        return this.rates.map(rate => rate.id);
    }

    *eachRate() {
        yield* this.rates;
    }
}
